if (!customElements.get('pc-treat-motion')) {
  customElements.define('pc-treat-motion', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;

      const video = this.querySelector('video');
      const toggle = this.parentElement?.querySelector('[data-pc-treat-toggle]');
      if (!(video instanceof HTMLVideoElement)) return;

      const controller = new AbortController();
      const options = { signal: controller.signal };
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let manualPause = false;
      let visible = false;
      let allowMotion = false;
      let frame = 0;
      let restartTimer = 0;
      let phase = 'idle';
      let failed = false;
      let playAttempt = 0;

      const setPhase = (value) => { phase = value; this.dataset.motionPhase = value; };

      // The legacy V21 source contains baked camera cuts near both ends. A brief
      // poster crossfade conceals the seek, but a genuinely seamless loop still
      // requires the planned source re-export with matched first/last frames.
      const startTime = 18 / 24;
      const exitTime = 256 / 24;
      video.loop = false;

      const permitted = () => visible && !manualPause && !document.hidden && (!motion.matches || allowMotion);

      const updateControl = () => {
        if (!(toggle instanceof HTMLButtonElement)) return;
        const paused = manualPause || (motion.matches && !allowMotion);
        toggle.setAttribute('aria-label', paused ? 'Play product animation' : 'Pause product animation');
        const icon = toggle.querySelector('[aria-hidden="true"]');
        if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
      };

      const cancelWatch = () => {
        cancelAnimationFrame(frame);
        frame = 0;
      };

      const fallback = () => {
        failed = true;
        playAttempt += 1;
        window.clearTimeout(restartTimer);
        cancelWatch();
        video.pause();
        delete this.dataset.ready;
        setPhase('failed');
        if (toggle instanceof HTMLButtonElement) toggle.hidden = true;
      };

      const load = () => {
        if (failed) return false;
        if (video.getAttribute('src')) return true;

        const ua = navigator.userAgent;
        const ios = /iP(?:hone|ad|od)/.test(ua)
          || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
        const desktopSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Android/.test(ua);
        const hevc = (ios || desktopSafari) && video.canPlayType('video/mp4; codecs="hvc1"');
        const source = hevc ? this.dataset.hevc : this.dataset.webm;

        if (!source) {
          fallback();
          return false;
        }

        // Assign exactly one source so Chromium never downloads the HEVC fallback
        // and Safari never downloads the WebM fallback.
        video.src = source;
        video.muted = true;
        video.preload = 'auto';
        video.load();
        return true;
      };

      const watch = () => {
        frame = 0;
        if (!permitted() || phase !== 'playing') return;

        // Do not replace the poster until the decoded video has advanced to a
        // meaningful frame. This prevents a transparent first frame from
        // creating a blank media stage on slow loads or background tabs.
        if (!this.hasAttribute('data-ready') && video.currentTime >= startTime + 0.35) {
          this.dataset.ready = '';
        }

        if (video.currentTime >= exitTime) {
          setPhase('restarting');
          video.pause();
          delete this.dataset.ready;
          restartTimer = window.setTimeout(() => {
            if (!this.isConnected) return;
            video.currentTime = startTime;
          }, 180);
          return;
        }

        frame = requestAnimationFrame(watch);
      };

      const playVideo = () => {
        const attempt = ++playAttempt;
        setPhase('starting');
        video.play().then(() => {
          if (attempt !== playAttempt || !this.isConnected || !permitted()) {
            video.pause();
            setPhase('paused');
            return;
          }

          setPhase('playing');
          cancelWatch();
          frame = requestAnimationFrame(watch);
        }).catch((error) => {
          if (attempt !== playAttempt) return;
          if (error.name === 'NotAllowedError') {
            manualPause = true;
            setPhase('paused');
            updateControl();
          } else if (error.name === 'AbortError') {
            setPhase('paused');
          } else {
            fallback();
          }
        });
      };

      const seekStart = () => {
        setPhase('seeking');
        video.currentTime = startTime;
      };

      const begin = () => {
        if (failed) return;
        if (motion.matches && !allowMotion) {
          updateControl();
          return;
        }

        setPhase('loading');
        if (!load()) return;
        if (video.readyState >= 1) seekStart();
      };

      const syncPlayback = () => {
        updateControl();
        if (failed) return;
        if (!permitted()) {
          playAttempt += 1;
          video.pause();
          cancelWatch();
          if (phase === 'starting' || phase === 'playing') setPhase('paused');
          return;
        }

        if (phase === 'idle') begin();
        else if (phase === 'playing' || phase === 'paused') playVideo();
      };

      video.addEventListener('loadedmetadata', () => { if (phase === 'loading') seekStart(); }, options);
      video.addEventListener('seeked', () => {
        if (phase !== 'seeking' && phase !== 'restarting') return;
        setPhase('paused');
        if (permitted()) playVideo();
      }, options);
      video.addEventListener('error', fallback, options);

      toggle?.addEventListener('click', () => {
        if (motion.matches && !allowMotion) {
          allowMotion = true;
          manualPause = false;
        } else {
          manualPause = !manualPause;
          if (manualPause && phase === 'playing') setPhase('paused');
        }
        syncPlayback();
      }, options);

      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        syncPlayback();
      }, { threshold: 0.05, rootMargin: '160px 0px' });

      observer.observe(this.parentElement);
      motion.addEventListener('change', () => {
        allowMotion = false;
        if (motion.matches) {
          video.pause();
          cancelWatch();
          delete this.dataset.ready;
          setPhase('idle');
        }
        syncPlayback();
      }, options);
      document.addEventListener('visibilitychange', syncPlayback, options);

      if (toggle instanceof HTMLButtonElement) toggle.hidden = false;
      updateControl();

      this.cleanup = () => {
        observer.disconnect();
        controller.abort();
        window.clearTimeout(restartTimer);
        playAttempt += 1;
        cancelWatch();
        video.pause();
      };
    }

    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  });
}
